import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { collection, query, where, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { sendFriendRequest, acceptFriendRequest, ignoreFriendRequest, removeFriend } from '../lib/firestore/friends';
import { generateInviteToken, generateInviteURL } from '../lib/invites';
import { generateFriendRequestQR, generateFriendRequestURL } from '../lib/friend-qr';
import { sendInviteSMS } from '../lib/sms';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import NotificationBell from '../components/NotificationBell';
import '../styles/Contacts.css';
import '../styles/Home.css';

const Contacts = () => {
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('contacts');
  const [loading, setLoading] = useState(true);
  
  // Header state
  const [userData, setUserData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const createDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '+1', email: '', note: '' });
  
  // Friends state
  const [friends, setFriends] = useState([]);
  
  // Invites state
  const [incomingInvites, setIncomingInvites] = useState([]);
  const [ignoredInvites, setIgnoredInvites] = useState([]);
  
  // Missed Connections state
  const [missedConnections, setMissedConnections] = useState([]);
  
  // QR Code state
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [qrCodeURL, setQrCodeURL] = useState('');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a dropdown item - if so, don't close (let the item's handler do it)
      const isDropdownItem = event.target.closest('.create-dropdown-item') || 
                             event.target.closest('.profile-dropdown-item');
      if (isDropdownItem) {
        return; // Let the dropdown item's onClick handle it
      }

      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setShowCreateDropdown(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showCreateDropdown || showProfileDropdown) {
      // Use 'click' instead of 'mousedown' to allow dropdown item clicks to fire first
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showCreateDropdown, showProfileDropdown]);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      loadUserData();
      loadLogo();
      loadAllData();
    }
  }, [user, authLoading, navigate]);

  const handleProfileClick = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  const refreshProfilePictureURL = async () => {
    if (!user) return null;
    try {
      const fileRef = ref(storage, `profile-pictures/${user.uid}`);
      const freshURL = await getDownloadURL(fileRef);
      // Update in Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        profilePicture: freshURL,
      });
      // Update state
      setUserData(prev => ({
        ...prev,
        profilePicture: freshURL
      }));
      return freshURL;
    } catch (error) {
      console.error('Error refreshing profile picture URL:', error);
      return null;
    }
  };

  const loadUserData = async () => {
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadLogo = async () => {
    try {
      const logoRef = ref(storage, 'hotelia-logo.png');
      const url = await getDownloadURL(logoRef);
      setLogoUrl(url);
    } catch (error) {
      console.log('⚠️ Logo not found in Storage');
      setLogoUrl(null);
    }
  };

  const loadAllData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Load each separately so we can see which one fails
      console.log('🔄 Starting to load all data...');
      await loadContacts();
      console.log('✅ Contacts loaded');
      await loadFriends();
      console.log('✅ Friends loaded');
      await loadInvites();
      console.log('✅ Invites loaded');
      await loadMissedConnections();
      console.log('✅ Missed connections loaded');
    } catch (error) {
      console.error('❌ Error loading data:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    if (!user) return;
    try {
      const contactsRef = collection(db, 'users', user.uid, 'contacts');
      const snapshot = await getDocs(contactsRef);
      console.log('Loaded contacts:', snapshot.docs.length);
      
      const contactsData = await Promise.all(
        snapshot.docs.map(async (contactDoc) => {
          try {
            console.log(`Processing contact: ${contactDoc.id}`);
            const contact = {
              id: contactDoc.id,
              ...contactDoc.data(),
              userData: null, // Will be populated if user exists
              isUser: false,
            };

            // Check if this contact is a user on Hotelia
            let foundUser = null;
            
            // Try to find by phone
            if (contact.phoneE164) {
              try {
                const usersRef = collection(db, 'users');
                const phoneDigits = contact.phoneE164.replace(/\D/g, ''); // Digits only
                
                console.log(`Checking for user with phone: ${contact.phoneE164} (digits: ${phoneDigits})`);
                
                // Try phone field (E.164 format)
                try {
                  console.log(`🔍 Querying users collection for phone: ${contact.phoneE164}`);
                  const phoneQuery = query(usersRef, where('phone', '==', contact.phoneE164));
                  console.log(`🔍 Query created, executing getDocs...`);
                  const phoneSnapshot = await getDocs(phoneQuery);
                  
                  console.log(`📊 Phone query (E.164) found ${phoneSnapshot.size} users`);
                  
                  if (!phoneSnapshot.empty) {
                    foundUser = phoneSnapshot.docs[0];
                    console.log(`✅ Found user by phone (E.164): ${foundUser.id}`, foundUser.data());
                  } else {
                    console.log(`❌ No users found with phone: ${contact.phoneE164}`);
                  }
                } catch (e164Error) {
                  console.error('❌ ERROR with E.164 phone query:', e164Error);
                  console.error('Error code:', e164Error?.code);
                  console.error('Error message:', e164Error?.message);
                  if (e164Error.code === 'failed-precondition') {
                    console.warn('⚠️ Missing index for phone field. Need to create index in Firebase Console.');
                    console.warn('   Collection: users, Field: phone');
                  } else if (e164Error.code === 'permission-denied') {
                    console.error('🚫 Permission denied - check Firestore security rules');
                  }
                }
                
                // Also try phoneNumber field (digits only) if E.164 didn't work
                if (!foundUser) {
                  try {
                    console.log(`🔍 Querying users collection for phoneNumber: ${phoneDigits}`);
                    const phoneNumberQuery = query(usersRef, where('phoneNumber', '==', phoneDigits));
                    console.log(`🔍 Query created, executing getDocs...`);
                    const phoneNumberSnapshot = await getDocs(phoneNumberQuery);
                    
                    console.log(`📊 Phone query (digits) found ${phoneNumberSnapshot.size} users`);
                    
                    if (!phoneNumberSnapshot.empty) {
                      foundUser = phoneNumberSnapshot.docs[0];
                      console.log(`✅ Found user by phoneNumber (digits): ${foundUser.id}`, foundUser.data());
                    } else {
                      console.log(`❌ No users found with phoneNumber: ${phoneDigits}`);
                    }
                  } catch (digitsError) {
                    console.error('❌ ERROR with digits phone query:', digitsError);
                    console.error('Error code:', digitsError?.code);
                    console.error('Error message:', digitsError?.message);
                    if (digitsError.code === 'failed-precondition') {
                      console.warn('⚠️ Missing index for phoneNumber field. Need to create index in Firebase Console.');
                      console.warn('   Collection: users, Field: phoneNumber');
                    } else if (digitsError.code === 'permission-denied') {
                      console.error('🚫 Permission denied - check Firestore security rules');
                    }
                  }
                }
                
                // If still not found, try loading current user's document to see what format is stored
                // This is a fallback for immediate debugging - will be replaced with scalable solution
                if (!foundUser) {
                  console.log(`❌ No user found for phone: ${contact.phoneE164}`);
                  console.log('🔍 STEP 1: Checking current user document to see phone format...');
                  console.log('🔍 STEP 2: Current user UID:', user.uid);
                  console.log('🔍 STEP 3: About to create doc reference...');
                  
                  try {
                    console.log('🔍 STEP 4: Creating doc reference...');
                    console.log('🔍 STEP 4a: db object:', db);
                    console.log('🔍 STEP 4b: user.uid:', user.uid);
                    console.log('🔍 STEP 4c: About to call doc() function...');
                    
                    let userDocRef;
                    try {
                      console.log('🔍 STEP 4d: Calling doc(db, "users", user.uid)...');
                      userDocRef = doc(db, 'users', user.uid);
                      console.log('🔍 STEP 5: Doc reference created successfully!');
                      console.log('🔍 STEP 5a: userDocRef:', userDocRef);
                      console.log('🔍 STEP 5b: Doc reference path:', userDocRef?.path);
                      console.log('🔍 STEP 5c: Doc reference id:', userDocRef?.id);
                      console.log('🔍 STEP 5d: Doc reference type:', typeof userDocRef);
                    } catch (docError) {
                      console.error('❌ ERROR creating doc reference:', docError);
                      console.error('Error details:', {
                        name: docError?.name,
                        message: docError?.message,
                        stack: docError?.stack
                      });
                      throw docError;
                    }
                    
                    if (!userDocRef) {
                      console.error('❌ userDocRef is null or undefined!');
                      return; // Exit early
                    }
                    
                    console.log('🔍 STEP 5e: userDocRef is valid, continuing...');
                    
                    console.log('🔍 STEP 6: About to call getDoc...');
                    
                    let currentUserDoc;
                    try {
                      console.log('🔍 STEP 7: Calling getDoc now...');
                      currentUserDoc = await getDoc(userDocRef);
                      console.log('🔍 STEP 8: getDoc completed successfully!');
                    } catch (getDocError) {
                      console.error('❌ ERROR in getDoc:', getDocError);
                      console.error('Error code:', getDocError?.code);
                      console.error('Error message:', getDocError?.message);
                      throw getDocError;
                    }
                    
                    console.log('🔍 STEP 9: User document exists?', currentUserDoc.exists());
                    
                    if (currentUserDoc.exists()) {
                      console.log('🔍 STEP 10: Document exists, getting data...');
                      const currentUserData = currentUserDoc.data();
                      console.log('✅ Current user document found!');
                      console.log('📱 Current user phone fields:', {
                        phone: currentUserData.phone,
                        phoneNumber: currentUserData.phoneNumber,
                        contactPhone: contact.phoneE164,
                        contactPhoneDigits: phoneDigits,
                        allFields: Object.keys(currentUserData)
                      });
                      
                      // Manual check if phone matches (try multiple formats)
                      const currentPhoneE164 = currentUserData.phone;
                      const currentPhoneDigits = currentUserData.phoneNumber || (currentPhoneE164 ? currentPhoneE164.replace(/\D/g, '') : '');
                      
                      console.log('🔍 Comparing phone numbers:', {
                        currentPhoneE164,
                        currentPhoneDigits,
                        contactPhoneE164: contact.phoneE164,
                        contactPhoneDigits: phoneDigits,
                        e164Match: currentPhoneE164 === contact.phoneE164,
                        digitsMatch: currentPhoneDigits === phoneDigits,
                        normalizedMatch: currentPhoneE164 && currentPhoneE164.replace(/\D/g, '') === phoneDigits
                      });
                      
                      if (currentPhoneE164 === contact.phoneE164 || 
                          currentPhoneDigits === phoneDigits ||
                          (currentPhoneE164 && currentPhoneE164.replace(/\D/g, '') === phoneDigits)) {
                        console.log('✅ Manual match found! Using current user document.');
                        foundUser = currentUserDoc;
                      } else {
                        console.log('❌ Phone numbers do not match:', {
                          currentPhoneE164,
                          currentPhoneDigits,
                          contactPhoneE164: contact.phoneE164,
                          contactPhoneDigits: phoneDigits
                        });
                      }
                    } else {
                      console.error('❌ STEP 11: Current user document does not exist in Firestore!');
                      console.error('This means the user document was never created during signup.');
                      console.error('User UID:', user.uid);
                      console.error('You may need to create the user document manually or re-signup.');
                    }
                  } catch (userDocError) {
                    console.error('❌ ERROR in user doc loading:', userDocError);
                    console.error('Error type:', typeof userDocError);
                    console.error('Error code:', userDocError?.code);
                    console.error('Error message:', userDocError?.message);
                    console.error('Error stack:', userDocError?.stack);
                    console.error('Full error object:', userDocError);
                  }
                } else {
                  console.log('✅ User already found via query, skipping manual check');
                }
                
                // TODO: Replace with scalable phoneToUser lookup collection
                // See SCALABLE_PHONE_LOOKUP.md for implementation plan
              } catch (phoneError) {
                console.error('Error checking phone for contact:', contact.name, phoneError);
              }
            }
          
          // Try to find by email if not found by phone
          if (!foundUser && contact.email) {
            try {
              const usersRef = collection(db, 'users');
              const emailQuery = query(usersRef, where('email', '==', contact.email));
              const emailSnapshot = await getDocs(emailQuery);
              if (!emailSnapshot.empty) {
                foundUser = emailSnapshot.docs[0];
              }
            } catch (emailError) {
              console.warn('Error checking email for contact:', contact.name, emailError);
            }
          }

          if (foundUser) {
            contact.userData = foundUser.data();
            contact.userId = foundUser.id;
            contact.isUser = true;
            
            console.log(`Contact ${contact.name} is a user: ${contact.userId} (current user: ${user.uid})`);
            
            // Check if already friends
            if (contact.userId !== user.uid) {
              const pairId = [user.uid, contact.userId].sort().join('_');
              const friendshipRef = doc(db, 'friendships', pairId);
              const friendship = await getDoc(friendshipRef);
              contact.isFriend = friendship.exists();
              
              // Check if friend request already sent
              const requestsRef = collection(db, 'friendRequests');
              const requestQuery = query(
                requestsRef,
                where('fromUserId', '==', user.uid),
                where('toUserId', '==', contact.userId),
                where('status', 'in', ['pending', 'accepted'])
              );
              const requestSnapshot = await getDocs(requestQuery);
              contact.hasPendingRequest = !requestSnapshot.empty;
            } else {
              contact.isFriend = false;
              contact.hasPendingRequest = false;
            }
          }

            return contact;
          } catch (contactError) {
            console.error(`❌ Error processing contact ${contactDoc.id}:`, contactError);
            console.error('Error details:', {
              name: contactError?.name,
              message: contactError?.message,
              stack: contactError?.stack
            });
            // Return a basic contact object so the list doesn't break
            return {
              id: contactDoc.id,
              ...contactDoc.data(),
              userData: null,
              isUser: false,
              error: contactError.message
            };
          }
        })
      );
      setContacts(contactsData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const loadFriends = async () => {
    if (!user) {
      console.log('❌ No user, skipping loadFriends');
      return;
    }
    try {
      console.log('🔄 LOADING FRIENDS - User ID:', user.uid);
      console.log('🔄 User UID type:', typeof user.uid, 'Length:', user.uid.length);
      
      // Get friendships where user is either 'a' or 'b'
      console.log('🔍 Querying friendships where user is "a" or "b"...');
      const friendshipsRef = collection(db, 'friendships');
      const q = query(
        friendshipsRef,
        where('a', '==', user.uid)
      );
      const q2 = query(
        friendshipsRef,
        where('b', '==', user.uid)
      );
      
      console.log('🔍 Query details:');
      console.log('  Query 1: where("a", "==", "' + user.uid + '")');
      console.log('  Query 2: where("b", "==", "' + user.uid + '")');
      
      let snapshot1, snapshot2;
      try {
        console.log('⏳ Executing queries...');
        snapshot1 = await getDocs(q);
        console.log('✅ Query 1 completed - Found:', snapshot1.size);
        snapshot2 = await getDocs(q2);
        console.log('✅ Query 2 completed - Found:', snapshot2.size);
      } catch (queryError) {
        console.error('❌❌❌ QUERY ERROR:', queryError);
        console.error('Query error code:', queryError.code);
        console.error('Query error message:', queryError.message);
        console.error('Query error stack:', queryError.stack);
        if (queryError.code === 'permission-denied') {
          console.error('🚫 PERMISSION DENIED - Check Firestore security rules!');
          console.error('🚫 The rules might be blocking the query. Check if isParty() function works correctly.');
        }
        throw queryError;
      }
      
      console.log('📊📊📊 QUERY RESULTS:');
      console.log(`  - Found ${snapshot1.size} friendships where user is 'a'`);
      console.log(`  - Found ${snapshot2.size} friendships where user is 'b'`);
      console.log(`  - TOTAL: ${snapshot1.size + snapshot2.size} friendships`);
      
      // Also try to directly read a friendship document to test permissions
      // If you accepted a request from lsOspxGJT1Ne7A8wGnKZmd0Oe0m1, the pairId should be:
      const testPairId = ['6OjCeWAguXUc8SjOzhq4wks9JFS2', 'lsOspxGJT1Ne7A8wGnKZmd0Oe0m1'].sort().join('_');
      console.log('🔍 Testing direct document read for pairId:', testPairId);
      try {
        const testFriendshipRef = doc(db, 'friendships', testPairId);
        const testFriendship = await getDoc(testFriendshipRef);
        if (testFriendship.exists()) {
          console.log('✅✅✅ FOUND FRIENDSHIP DOCUMENT!', testFriendship.data());
          console.log('⚠️ But queries returned 0 results - this is a QUERY PERMISSIONS ISSUE!');
        } else {
          console.log('❌ Friendship document does not exist at:', testPairId);
          console.log('⚠️ This means the friendship was never created when you accepted the request!');
        }
      } catch (testError) {
        console.error('❌ Error reading test friendship:', testError);
        if (testError.code === 'permission-denied') {
          console.error('🚫🚫🚫 PERMISSION DENIED reading friendship document!');
          console.error('🚫 The security rules are blocking read access!');
        }
      }
      
      // Log each document found
      if (snapshot1.size > 0) {
        console.log('📋 Friendships where user is "a":');
        snapshot1.docs.forEach(doc => {
          const data = doc.data();
          console.log(`  - ${doc.id}: a="${data.a}", b="${data.b}"`);
        });
      }
      if (snapshot2.size > 0) {
        console.log('📋 Friendships where user is "b":');
        snapshot2.docs.forEach(doc => {
          const data = doc.data();
          console.log(`  - ${doc.id}: a="${data.a}", b="${data.b}"`);
        });
      }
      
      // Log the actual documents for debugging
      snapshot1.docs.forEach(doc => {
        console.log(`  - Friendship doc (a): ${doc.id}`, doc.data());
      });
      snapshot2.docs.forEach(doc => {
        console.log(`  - Friendship doc (b): ${doc.id}`, doc.data());
      });
      
      const allFriendships = [
        ...snapshot1.docs.map(doc => {
          const data = doc.data();
          return { 
            id: doc.id, 
            ...data, 
            otherUserId: data.b,
            since: data.since
          };
        }),
        ...snapshot2.docs.map(doc => {
          const data = doc.data();
          return { 
            id: doc.id, 
            ...data, 
            otherUserId: data.a,
            since: data.since
          };
        }),
      ];
      
      console.log(`📊 Total friendships found: ${allFriendships.length}`);
      console.log('📋 All friendships:', allFriendships);
      
      if (allFriendships.length === 0) {
        console.warn('⚠️⚠️⚠️ NO FRIENDSHIPS FOUND FOR USER:', user.uid);
        console.warn('⚠️ Checking accepted friend requests to see if friendship was created...');
        // Check if there are any accepted friend requests that should have created friendships
        try {
          const requestsRef = collection(db, 'friendRequests');
          const acceptedQuery = query(
            requestsRef,
            where('status', '==', 'accepted')
          );
          const acceptedSnapshot = await getDocs(acceptedQuery);
          console.log(`📋 Found ${acceptedSnapshot.size} accepted friend requests`);
          
          if (acceptedSnapshot.size > 0) {
            console.warn('⚠️ Found accepted requests but no friendships! This might indicate:');
            console.warn('  1. Friendship document was not created when request was accepted');
            console.warn('  2. User IDs in friendship don\'t match current user');
            acceptedSnapshot.docs.forEach(doc => {
              const data = doc.data();
              const isFromUser = data.fromUserId === user.uid;
              const isToUser = data.toUserId === user.uid;
              if (isFromUser || isToUser) {
                console.warn(`  - Request ${doc.id}: from="${data.fromUserId}", to="${data.toUserId}"`);
                console.warn(`    User is ${isFromUser ? 'sender' : 'recipient'}`);
                // Check what the pairId should be
                const pairId = [data.fromUserId, data.toUserId].sort().join('_');
                console.warn(`    Expected friendship ID: ${pairId}`);
              }
            });
          } else {
            console.warn('⚠️ No accepted friend requests found.');
            console.warn('⚠️ Make sure you accepted a friend request from the Invites tab.');
          }
        } catch (requestError) {
          console.error('❌ Error checking accepted requests:', requestError);
        }
      }
      
      // Get user data for friends
      const friendsData = await Promise.all(
        allFriendships.map(async (friendship) => {
          const otherUserId = friendship.otherUserId;
          console.log(`👤 Loading user data for friend: ${otherUserId}`);
          try {
            const userDoc = await getDoc(doc(db, 'users', otherUserId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              console.log(`✅ Found user data for ${otherUserId}:`, userData.firstName, userData.lastName);
              return {
                ...friendship,
                userData: userData,
              };
            } else {
              console.warn(`⚠️ User document not found for friend: ${otherUserId}`);
              return null;
            }
          } catch (userError) {
            console.error(`❌ Error loading user data for ${otherUserId}:`, userError);
            return null;
          }
        })
      );
      
      const validFriends = friendsData.filter(f => f && f.userData);
      console.log(`✅ Valid friends (with user data): ${validFriends.length}`);
      console.log('👥 Final friends list:', validFriends);
      setFriends(validFriends);
    } catch (error) {
      console.error('❌ Error loading friends:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Set empty array on error so UI doesn't break
      setFriends([]);
    }
  };

  const loadInvites = async () => {
    if (!user) return;
    try {
      const invitesRef = collection(db, 'friendRequests');
      const q = query(
        invitesRef,
        where('toUserId', '==', user.uid),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      const invites = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const fromUserDoc = await getDoc(doc(db, 'users', data.fromUserId));
          return {
            id: docSnap.id,
            ...data,
            fromUserData: fromUserDoc.exists() ? fromUserDoc.data() : null,
          };
        })
      );
      setIncomingInvites(invites.filter(inv => inv.fromUserData));
      
      // Load ignored invites
      const ignoredQ = query(
        invitesRef,
        where('toUserId', '==', user.uid),
        where('status', '==', 'ignored')
      );
      const ignoredSnapshot = await getDocs(ignoredQ);
      const ignored = await Promise.all(
        ignoredSnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const fromUserDoc = await getDoc(doc(db, 'users', data.fromUserId));
          return {
            id: docSnap.id,
            ...data,
            fromUserData: fromUserDoc.exists() ? fromUserDoc.data() : null,
          };
        })
      );
      setIgnoredInvites(ignored.filter(inv => inv.fromUserData));
    } catch (error) {
      console.error('Error loading invites:', error);
    }
  };

  const loadMissedConnections = async () => {
    if (!user) return;
    try {
      const missedRef = collection(db, 'users', user.uid, 'missedConnections');
      const snapshot = await getDocs(missedRef);
      const connections = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const otherUserId = docSnap.id;
          const userDoc = await getDoc(doc(db, 'users', otherUserId));
          return {
            id: docSnap.id,
            ...data,
            userData: userDoc.exists() ? userDoc.data() : null,
          };
        })
      );
      
      // Sort by sharedCount descending
      const sorted = connections
        .filter(c => c.userData)
        .sort((a, b) => (b.sharedCount || 0) - (a.sharedCount || 0));
      setMissedConnections(sorted);
    } catch (error) {
      console.error('Error loading missed connections:', error);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('You must be logged in to add contacts');
      return;
    }
    
    // Validate phone number is required
    if (!newContact.phone || !newContact.phone.trim()) {
      alert('Phone number is required for contacts');
      return;
    }
    
    // Validate name
    if (!newContact.name || !newContact.name.trim()) {
      alert('Name is required for contacts');
      return;
    }
    
    // PhoneInput already provides E.164 format, but ensure it's valid
    const phoneE164 = newContact.phone.trim();
    if (!phoneE164.startsWith('+')) {
      alert('Please enter a valid phone number with country code');
      return;
    }
    
    try {
      console.log('Adding contact:', { name: newContact.name, phone: phoneE164, email: newContact.email });
      
      // Check if contact already exists (by phone number) - prevents duplicates including yourself
      const contactsRef = collection(db, 'users', user.uid, 'contacts');
      const phoneDigits = phoneE164.replace(/\D/g, ''); // Digits only for phoneNumber field
      
      // Load all contacts to check for duplicates (more reliable than query if index is missing)
      const allContactsSnapshot = await getDocs(contactsRef);
      const existingContact = allContactsSnapshot.docs.find(doc => {
        const data = doc.data();
        const existingPhoneDigits = data.phoneE164 ? data.phoneE164.replace(/\D/g, '') : '';
        return data.phoneE164 === phoneE164 || existingPhoneDigits === phoneDigits;
      });
      
      if (existingContact) {
        console.log('Contact already exists:', { id: existingContact.id, ...existingContact.data() });
        alert('This contact already exists in your contacts list');
        // Reload contacts to show the existing one
        await loadContacts();
        return;
      }
      
      // Check if this phone number belongs to an existing user (for display purposes)
      let foundUser = null;
      
      try {
        const usersRef = collection(db, 'users');
        
        // Try to find user by phone (E.164 format)
        const phoneQuery = query(usersRef, where('phone', '==', phoneE164));
        const phoneSnapshot = await getDocs(phoneQuery);
        
        if (!phoneSnapshot.empty) {
          foundUser = phoneSnapshot.docs[0];
        } else {
          // Also try phoneNumber field (digits only)
          const phoneNumberQuery = query(usersRef, where('phoneNumber', '==', phoneDigits));
          const phoneNumberSnapshot = await getDocs(phoneNumberQuery);
          if (!phoneNumberSnapshot.empty) {
            foundUser = phoneNumberSnapshot.docs[0];
          }
        }
      } catch (userQueryError) {
        console.warn('Error checking for existing user:', userQueryError);
        // Continue - this is optional for display
      }
      
      // Also check by email if provided
      if (!foundUser && newContact.email && newContact.email.trim()) {
        try {
          const usersRef = collection(db, 'users');
          const emailQuery = query(usersRef, where('email', '==', newContact.email.trim()));
          const emailSnapshot = await getDocs(emailQuery);
          if (!emailSnapshot.empty) {
            foundUser = emailSnapshot.docs[0];
          }
        } catch (emailQueryError) {
          console.warn('Error checking email:', emailQueryError);
          // Continue - this is optional
        }
      }
      
      // Add the contact
      const contactData = {
        name: newContact.name.trim(),
        phoneE164: phoneE164,
        email: newContact.email.trim() || null,
        note: newContact.note.trim() || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      console.log('Adding contact with data:', contactData);
      
      const docRef = await addDoc(contactsRef, contactData);
      console.log('✅ Contact added successfully with ID:', docRef.id);
      
      setNewContact({ name: '', phone: '+1', email: '', note: '' });
      setShowAddContact(false);
      
      // Reload contacts to get the updated list with user data
      await loadContacts();
      
      alert('Contact added successfully!');
    } catch (error) {
      console.error('❌ Error adding contact:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      let errorMessage = 'Failed to add contact. ';
      
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check your Firestore security rules.';
      } else if (error.code === 'failed-precondition') {
        errorMessage += 'Missing Firestore index. Please create the index for phoneE164 queries in Firebase Console.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }
      
      alert(errorMessage);
    }
  };

  const handleInviteFriends = async () => {
    if (!user) return;
    
    try {
      // Calculate display name for metadata
      const inviterDisplayName = userData?.firstName || userData?.lastName
        ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
        : 'Friend';
      
      // Generate invite token for general signup (contact_invite kind)
      const token = await generateInviteToken(
        {
          kind: 'contact_invite',
          id: user.uid, // Inviter's user ID
        },
        user.uid,
        {
          oneTime: false, // Can be used multiple times
          expiresIn: 90 * 24 * 60 * 60, // 90 days
          metadata: {
            inviterName: inviterDisplayName,
            inviterPhone: userData?.phone || userData?.phoneNumber || null,
          },
        }
      );

      const inviteURL = generateInviteURL(token);
      
      // Show share options
      const shareText = `Join me on Hotelia! ${inviteURL}`;
      
      // Try native share API first (mobile - works great on Chrome/Edge)
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join Hotelia',
            text: shareText,
            url: inviteURL,
          });
          return; // Native share worked
        } catch (err) {
          // User cancelled or share failed, fall through to clipboard
          if (err.name === 'AbortError') {
            // User cancelled - don't show error, just return
            return;
          }
          // Other errors - continue to clipboard fallback
        }
      }
      
      // Fallback: Try to copy to clipboard
      let clipboardSuccess = false;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(inviteURL);
          clipboardSuccess = true;
        } catch (clipboardError) {
          // Clipboard failed (Safari permissions, etc.) - show link in modal instead
          console.warn('Clipboard access denied:', clipboardError);
          clipboardSuccess = false;
        }
      }
      
      // Show the link in a user-friendly way
      if (clipboardSuccess) {
        alert(`✅ Invite link copied to clipboard!\n\n${inviteURL}\n\nShare this link with friends via text or email. When they sign up, they'll automatically be added to your contacts!`);
      } else {
        // Clipboard failed - show link prominently so user can copy manually
        const userConfirmed = confirm(
          `📋 Copy this invite link:\n\n${inviteURL}\n\n` +
          `Click OK to see the link again, then copy it manually.\n\n` +
          `Share this link with friends via text or email. When they sign up, they'll automatically be added to your contacts!`
        );
        
        if (userConfirmed) {
          // Show in a way that's easy to select and copy
          const linkText = prompt(
            'Copy this invite link (select all and copy):',
            inviteURL
          );
          // User can now manually copy from the prompt
        }
      }
    } catch (error) {
      console.error('Error generating invite:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      alert(`Failed to generate invite: ${error.message || 'Unknown error'}. Please check the console for details.`);
    }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        alert('CSV file is empty');
        return;
      }

      // Parse CSV (simple parser - assumes comma-separated, no quotes handling for MVP)
      const contacts = [];
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
      
      // Find column indices
      const nameIndex = headers.findIndex(h => h.includes('name') || h.includes('contact'));
      const phoneIndex = headers.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('cell'));
      const emailIndex = headers.findIndex(h => h.includes('email') || h.includes('e-mail'));
      const noteIndex = headers.findIndex(h => h.includes('note') || h.includes('note'));

      if (nameIndex === -1) {
        alert('CSV must have a "name" column');
        return;
      }

      // Parse rows
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const name = values[nameIndex]?.trim();
        
        if (!name) continue; // Skip rows without names

        const phone = phoneIndex >= 0 ? values[phoneIndex]?.trim() : '';
        const email = emailIndex >= 0 ? values[emailIndex]?.trim() : '';
        const note = noteIndex >= 0 ? values[noteIndex]?.trim() : '';

        // Normalize phone to E.164 format (basic - assumes US if starts with number)
        let phoneE164 = null;
        if (phone) {
          // Remove non-digits
          const digits = phone.replace(/\D/g, '');
          if (digits.length === 10) {
            phoneE164 = '+1' + digits; // US default
          } else if (digits.length === 11 && digits[0] === '1') {
            phoneE164 = '+' + digits;
          } else if (phone.startsWith('+')) {
            phoneE164 = phone; // Already in E.164 format
          }
        }

        // Only add contacts with phone numbers
        if (phoneE164) {
          contacts.push({
            name,
            phoneE164,
            email: email || null,
            note: note || null,
          });
        }
      }

      if (contacts.length === 0) {
        alert('No valid contacts found in CSV. Contacts must have phone numbers.');
        return;
      }

      // Batch upload contacts (Firestore batch limit is 500, we'll do 200 at a time)
      const batchSize = 200;
      const contactsRef = collection(db, 'users', user.uid, 'contacts');
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < contacts.length; i += batchSize) {
        const batch = contacts.slice(i, i + batchSize);
        
        try {
          const batchPromises = batch.map(contact => 
            addDoc(contactsRef, {
              ...contact,
              createdAt: new Date(),
              updatedAt: new Date(),
            })
          );
          
          await Promise.all(batchPromises);
          successCount += batch.length;
        } catch (error) {
          console.error(`Error importing batch starting at ${i}:`, error);
          errorCount += batch.length;
        }
      }

      // Reset file input
      e.target.value = '';

      alert(`Import complete!\n${successCount} contacts imported successfully${errorCount > 0 ? `\n${errorCount} contacts failed to import` : ''}`);
      await loadContacts();
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Failed to import CSV. Please check the format and try again.\n\nExpected format: Name, Phone, Email, Note (with headers)');
    }
  };

  const handleSendFriendRequest = async (contact) => {
    if (!user || !contact.userId) return;
    
    try {
      await sendFriendRequest(user.uid, contact.userId);
      alert('Friend request sent!');
      await Promise.all([loadContacts(), loadInvites()]);
    } catch (error) {
      console.error('Error sending friend request:', error);
      alert(error.message || 'Failed to send friend request. Please try again.');
    }
  };

  const handleInviteToHotelia = async (contact) => {
    if (!user) return;
    
    if (!contact.phoneE164) {
      alert('Phone number is required to send an SMS invite.');
      return;
    }
    
    try {
      // Generate invite token for general signup (profile_follow kind)
      const token = await generateInviteToken(
        {
          kind: 'profile_follow',
          id: user.uid, // They'll follow you when they sign up
        },
        user.uid,
        {
          oneTime: false,
          expiresIn: 30 * 24 * 60 * 60, // 30 days
          metadata: {
            contactName: contact.name,
            contactPhone: contact.phoneE164,
          },
        }
      );

      const inviteURL = generateInviteURL(token);
      
      // Try to send SMS
      try {
        const inviterName = `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || 'Someone';
        // Check if contact is already a user (has userId) for consent checking
        const contactUserId = contact.userId || contact.userData?.userId;
        await sendInviteSMS(
          contact.phoneE164, 
          inviteURL, 
          inviterName,
          contactUserId, // recipientUserId - only if they're already a user
          user.uid // senderUserId for friends_only check
        );
        alert(`✅ SMS invite sent to ${contact.name}!\n\nThey'll receive a text message with a link to join Hotelia.`);
      } catch (smsError) {
        // Check if error is due to user opting out
        if (smsError.message && smsError.message.includes('opted out')) {
          alert(`⚠️ ${contact.name} has opted out of receiving SMS messages. Invite link copied to clipboard instead.`);
          await navigator.clipboard.writeText(inviteURL);
        } else {
          console.error('SMS sending failed, falling back to clipboard:', smsError);
          // Fallback: copy to clipboard
          await navigator.clipboard.writeText(inviteURL);
          alert(`⚠️ SMS service not configured. Invite link copied to clipboard!\n\nShare this link with ${contact.name}: ${inviteURL}\n\nTo enable SMS, set up Twilio and configure the backend API endpoint.`);
        }
      }
    } catch (error) {
      console.error('Error generating invite:', error);
      alert('Failed to generate invite. Please try again.');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!user || !confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      const contactRef = doc(db, 'users', user.uid, 'contacts', contactId);
      await deleteDoc(contactRef);
      await loadContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact. Please try again.');
    }
  };

  const handleAcceptInvite = async (requestId) => {
    if (!user) return;
    
    try {
      // Get the request to find the sender's info before accepting
      const requestRef = doc(db, 'friendRequests', requestId);
      const requestDoc = await getDoc(requestRef);
      
      if (!requestDoc.exists()) {
        throw new Error('Friend request not found');
      }
      
      const requestData = requestDoc.data();
      const fromUserId = requestData.fromUserId;
      
      // Accept the friend request
      await acceptFriendRequest(requestId, user.uid);
      
      // Get the sender's user data to add to contacts
      const fromUserDoc = await getDoc(doc(db, 'users', fromUserId));
      if (fromUserDoc.exists()) {
        const fromUserData = fromUserDoc.data();
        const fromUserPhone = fromUserData.phone || fromUserData.phoneNumber;
        
        if (fromUserPhone) {
          // Check if already in contacts
          const contactsRef = collection(db, 'users', user.uid, 'contacts');
          const phoneE164 = fromUserPhone.startsWith('+') ? fromUserPhone : `+${fromUserPhone}`;
          const phoneDigits = phoneE164.replace(/\D/g, '');
          
          // Try to find by phoneE164
          const existingQuery = query(
            contactsRef,
            where('phoneE164', '==', phoneE164)
          );
          const existing = await getDocs(existingQuery);
          
          if (existing.empty) {
            // Also try by digits-only format
            const existingDigitsQuery = query(
              contactsRef,
              where('phoneE164', '==', `+${phoneDigits}`)
            );
            const existingDigits = await getDocs(existingDigitsQuery);
            
            if (existingDigits.empty) {
              // Add to contacts
              await addDoc(contactsRef, {
                name: `${fromUserData.firstName || ''} ${fromUserData.lastName || ''}`.trim() || 'Friend',
                phoneE164: phoneE164,
                email: fromUserData.email || null,
                note: 'Added when accepting friend request',
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
              });
            }
          }
        }
      }
      
      alert('Friend request accepted! They\'ve been added to your contacts.');
      await Promise.all([loadFriends(), loadInvites(), loadContacts()]);
    } catch (error) {
      console.error('Error accepting invite:', error);
      alert(error.message || 'Failed to accept invite. Please try again.');
    }
  };

  const handleIgnoreInvite = async (requestId) => {
    if (!user) return;
    
    try {
      await ignoreFriendRequest(requestId, user.uid);
      await loadInvites();
    } catch (error) {
      console.error('Error ignoring invite:', error);
      alert(error.message || 'Failed to ignore invite. Please try again.');
    }
  };

  const handleRemoveFriend = async (friendshipId, otherUserId) => {
    if (!user || !confirm('Are you sure you want to remove this friend?')) return;
    
    try {
      await removeFriend(user.uid, otherUserId);
      await loadFriends();
    } catch (error) {
      console.error('Error removing friend:', error);
      alert(error.message || 'Failed to remove friend. Please try again.');
    }
  };

  const handleShowQR = async () => {
    if (!user) return;
    
    try {
      const qrData = await generateFriendRequestQR(user.uid);
      const qrURL = generateFriendRequestURL(user.uid);
      setQrCodeData(qrData);
      setQrCodeURL(qrURL);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code. Please try again.');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="contacts-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const displayName = userData?.firstName || userData?.lastName
    ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
    : 'Friend';

  return (
    <div className="contacts-container">
      <header className="home-header">
        {/* Desktop: Logo on left, profile in middle, actions on right */}
        <div className="header-left desktop-only">
          {/* Hotelia Logo - Desktop only */}
          <div 
            className="hotelia-logo" 
            onClick={() => navigate('/home')}
            title="Go to Home"
          >
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Hotelia" 
                className="logo-image"
                onError={(e) => {
                  console.error('Failed to load logo image:', logoUrl);
                  setLogoUrl(null);
                }}
                onLoad={() => {
                  console.log('✅ Logo image loaded successfully');
                }}
              />
            ) : (
              <span className="logo-text">HOTELIA</span>
            )}
          </div>
        </div>
        
        {/* Mobile/Tablet: Profile picture and name on left */}
        <div className="profile-section-mobile mobile-only" ref={profileDropdownRef}>
          <div className="profile-picture-wrapper-mobile" onClick={handleProfileClick}>
            {userData?.profilePicture ? (
              <img
                src={userData.profilePicture}
                alt="Profile"
                className="profile-picture"
                onError={async (e) => {
                  console.error('Failed to load profile picture');
                  // Try to refresh the URL - might be expired token
                  const freshURL = await refreshProfilePictureURL();
                  if (!freshURL) {
                    // If refresh fails, remove from state
                    setUserData(prev => {
                      const updated = { ...prev };
                      delete updated.profilePicture;
                      return updated;
                    });
                  }
                }}
              />
            ) : (
              <div className="profile-picture-placeholder">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <h1 className="profile-name-mobile" onClick={handleProfileClick}>
            {displayName}
          </h1>
          {showProfileDropdown && (
            <div className="profile-dropdown">
              <div className="profile-dropdown-item" onClick={() => navigate('/home')}>
                <div className="profile-dropdown-title">🏠 Home</div>
                <div className="profile-dropdown-description">
                  Go back to home
                </div>
              </div>
              <div className="profile-dropdown-item" onClick={() => navigate('/settings')}>
                <div className="profile-dropdown-title">⚙️ Settings</div>
                <div className="profile-dropdown-description">
                  Manage your account
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: Profile section in middle */}
        <div className="profile-section desktop-only">
          <div className="profile-picture-wrapper" onClick={() => navigate('/home')}>
            {userData?.profilePicture ? (
              <img
                src={userData.profilePicture}
                alt="Profile"
                className="profile-picture"
                onError={async (e) => {
                  console.error('Failed to load profile picture');
                  // Try to refresh the URL - might be expired token
                  const freshURL = await refreshProfilePictureURL();
                  if (!freshURL) {
                    // If refresh fails, remove from state
                    setUserData(prev => {
                      const updated = { ...prev };
                      delete updated.profilePicture;
                      return updated;
                    });
                  }
                }}
              />
            ) : (
              <div className="profile-picture-placeholder">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <h1 className="profile-name" onClick={() => navigate('/home')}>
            {displayName}
          </h1>
        </div>

        <div className="header-actions">
          {/* Icon-only buttons (same on mobile and desktop) */}
          <div className="header-icons">
            <button
              className="btn-icon-flat contacts-icon"
              onClick={() => navigate('/contacts')}
              title="Contacts"
              style={{ backgroundColor: 'rgba(0, 217, 255, 0.15)' }}
            >
              📇
            </button>
            <NotificationBell />
            <button
              className="btn-icon-flat settings-icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/settings');
              }}
              title="Settings"
            >
              ⚙️
            </button>
          </div>
        </div>
        
      </header>

      {/* Mobile: Unified tabs + create button header */}
      <div className="contacts-tabs-mobile mobile-only">
        <div className="contacts-tabs-left">
          <button
            className={`contacts-tab-mobile ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts
          </button>
          <button
            className={`contacts-tab-mobile ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            Friends
          </button>
        </div>
        
        {/* Floating + Button to Show QR Code */}
        <div className="contacts-create-wrapper">
          <button
            className="contacts-create-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleShowQR();
            }}
            title="Show My QR Code"
          >
            +
          </button>
        </div>
        
        <div className="contacts-tabs-right">
          <button
            className={`contacts-tab-mobile ${activeTab === 'invites' ? 'active' : ''}`}
            onClick={() => setActiveTab('invites')}
          >
            Invites
          </button>
          <button
            className={`contacts-tab-mobile ${activeTab === 'missed' ? 'active' : ''}`}
            onClick={() => setActiveTab('missed')}
          >
            Missed C
          </button>
        </div>
      </div>

      {/* Desktop: Regular tabs */}
      <div className="contacts-tabs desktop-only">
        <button
          className={`contacts-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          Contacts
        </button>
        <button
          className={`contacts-tab ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          Friends
        </button>
        <button
          className={`contacts-tab ${activeTab === 'invites' ? 'active' : ''}`}
          onClick={() => setActiveTab('invites')}
        >
          Invites
        </button>
        <button
          className={`contacts-tab ${activeTab === 'missed' ? 'active' : ''}`}
          onClick={() => setActiveTab('missed')}
        >
          Missed Connections
        </button>
      </div>

      <main className="contacts-main">
        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="contacts-tab-content">
            <div className="contacts-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowAddContact(true)}
              >
                + Add Contact
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleInviteFriends}
                title="Get a shareable link to invite friends"
              >
                ✉️ Invite Friends
              </button>
              <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                Import CSV
                <input
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleCSVImport}
                />
              </label>
            </div>

            {showAddContact && (
              <div className="add-contact-modal">
                <div className="modal-content">
                  <h2>Add Contact</h2>
                  <form onSubmit={handleAddContact}>
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        className="input"
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone *</label>
                      <PhoneInput
                        international
                        defaultCountry="US"
                        value={newContact.phone}
                        onChange={(value) => setNewContact({ ...newContact, phone: value || '+1' })}
                        className="phone-input"
                        required
                      />
                      <small className="form-hint">Phone number is required</small>
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        className="input"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Note</label>
                      <textarea
                        className="input"
                        value={newContact.note}
                        onChange={(e) => setNewContact({ ...newContact, note: e.target.value })}
                        rows="3"
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary">
                        Add
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowAddContact(false);
                          setNewContact({ name: '', phone: '+1', email: '', note: '' });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="contacts-list">
              {contacts.length === 0 ? (
                <div className="empty-state">
                  <p>No contacts yet. Add your first contact!</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div key={contact.id} className="contact-item">
                    <div className="contact-info">
                      <h3>{contact.name}</h3>
                      {contact.isUser && contact.userData && (
                        <p className="contact-username">
                          @{`${contact.userData.firstName || ''} ${contact.userData.lastName || ''}`.trim() || 'User'}
                        </p>
                      )}
                      {contact.phoneE164 && <p>📞 {contact.phoneE164}</p>}
                      {contact.email && <p>✉️ {contact.email}</p>}
                      {contact.note && <p className="contact-note">{contact.note}</p>}
                    </div>
                    <div className="contact-actions">
                      {contact.isUser && contact.userId !== user?.uid ? (
                        // They're a user - show friend status or button
                        contact.isFriend ? (
                          <span className="contact-status">Friends</span>
                        ) : contact.hasPendingRequest ? (
                          <span className="contact-status">Request Sent</span>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSendFriendRequest(contact)}
                          >
                            Be Friends?
                          </button>
                        )
                      ) : contact.isUser && contact.userId === user?.uid ? (
                        <span className="contact-status">You</span>
                      ) : (
                        // Not a user - show invite button
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleInviteToHotelia(contact)}
                        >
                          Invite to Hotelia
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteContact(contact.id)}
                        title="Delete contact"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Friends Tab */}
        {activeTab === 'friends' && (
          <div className="contacts-tab-content">
            <div className="contacts-actions">
              <button
                className="btn btn-primary qr-button"
                onClick={handleShowQR}
                title="Show QR code for others to scan and send you a friend request"
              >
                📱 Show My QR
              </button>
            </div>
            <div className="contacts-list">
              {friends.length === 0 ? (
                <div className="empty-state">
                  <p>No friends yet. Send friend requests to your contacts!</p>
                </div>
              ) : (
                friends.map((friendship) => (
                  <div key={friendship.id} className="contact-item">
                    <div className="contact-info">
                      <h3>
                        {friendship.userData?.firstName || ''} {friendship.userData?.lastName || ''}
                      </h3>
                      {friendship.userData?.email && <p>✉️ {friendship.userData.email}</p>}
                      {friendship.userData?.phone && <p>📞 {friendship.userData.phone}</p>}
                      <p className="contact-meta">
                        Friends since {friendship.since?.toDate?.().toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleRemoveFriend(friendship.id, friendship.otherUserId)}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Invites Tab */}
        {activeTab === 'invites' && (
          <div className="contacts-tab-content">
            {incomingInvites.length === 0 && ignoredInvites.length === 0 ? (
              <div className="empty-state">
                <p>No friend requests yet.</p>
              </div>
            ) : (
              <>
                {incomingInvites.length > 0 && (
                  <div className="invites-section">
                    <h3>Pending Requests</h3>
                    <div className="contacts-list">
                      {incomingInvites.map((invite) => (
                        <div key={invite.id} className="contact-item">
                          <div className="contact-info">
                            <h3>
                              {invite.fromUserData?.firstName || ''} {invite.fromUserData?.lastName || ''}
                            </h3>
                            {invite.fromUserData?.email && <p>✉️ {invite.fromUserData.email}</p>}
                          </div>
                          <div className="contact-actions">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleAcceptInvite(invite.id)}
                            >
                              Accept
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleIgnoreInvite(invite.id)}
                            >
                              Ignore
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {ignoredInvites.length > 0 && (
                  <div className="invites-section">
                    <details>
                      <summary>Ignored ({ignoredInvites.length})</summary>
                      <div className="contacts-list">
                        {ignoredInvites.map((invite) => (
                          <div key={invite.id} className="contact-item ignored">
                            <div className="contact-info">
                              <h3>
                                {invite.fromUserData?.firstName || ''} {invite.fromUserData?.lastName || ''}
                              </h3>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Missed Connections Tab */}
        {activeTab === 'missed' && (
          <div className="contacts-tab-content">
            <p className="section-description">
              People you've attended events with, sorted by how many events you've shared.
            </p>
            <div className="contacts-list">
              {missedConnections.length === 0 ? (
                <div className="empty-state">
                  <p>No missed connections yet. Check in to events to see people you've shared experiences with!</p>
                </div>
              ) : (
                missedConnections.map((connection) => (
                  <div key={connection.id} className="contact-item">
                    <div className="contact-info">
                      <h3>
                        {connection.userData?.firstName || ''} {connection.userData?.lastName || ''}
                      </h3>
                      <p className="contact-meta">
                        {connection.sharedCount || 0} shared event{connection.sharedCount !== 1 ? 's' : ''}
                      </p>
                      {connection.lastSharedAt && (
                        <p className="contact-meta">
                          Last: {connection.lastSharedAt.toDate?.().toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
          </main>

          {/* QR Code Modal */}
          {showQRModal && (
            <div className="qr-modal" onClick={() => setShowQRModal(false)}>
              <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="qr-modal-header">
                  <h2>Scan to Add Me</h2>
                  <button
                    className="qr-modal-close"
                    onClick={() => setShowQRModal(false)}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="qr-modal-body">
                  <p className="qr-instructions">
                    Have someone scan this QR code with their Hotelia app or camera to automatically send you a friend request!
                  </p>
                  {qrCodeData && (
                    <div className="qr-code-container">
                      <img src={qrCodeData} alt="Friend Request QR Code" className="qr-code-image" />
                    </div>
                  )}
                  <div className="qr-url-container">
                    <p className="qr-url-label">Or share this link:</p>
                    <div className="qr-url-display">
                      <input
                        type="text"
                        readOnly
                        value={qrCodeURL}
                        className="qr-url-input"
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(qrCodeURL);
                            alert('Link copied to clipboard!');
                          } catch (err) {
                            console.error('Failed to copy:', err);
                          }
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    export default Contacts;

